import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import ProjectCard from "@/components/ProjectCard";
import { Button } from "@/components/ui/button";
import api, { money } from "@/lib/api";
import { ShieldCheck, Smartphone, TrendingUp, Users, ArrowRight, Landmark, Star, CheckCircle2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1729006122953-e3cafd3dd875?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxLaW5zaGFzYXxlbnwwfHx8fDE3ODYzODU1NjJ8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-congo-blue overflow-hidden">
        <img src={HERO} alt="Kinshasa" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="eager" />
        <div className="absolute inset-0 bg-congo-bluedark/70" />
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28">
          <div className="max-w-2xl animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-white text-xs font-semibold mb-6">
              <Star className="h-3.5 w-3.5 text-congo-gold fill-congo-gold" /> Investir dans l'avenir de la RDC
            </div>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]">
              Investissez dans les grands projets nationaux, <span className="text-congo-gold">dès 50$</span>
            </h1>
            <p className="mt-6 text-lg text-blue-100 leading-relaxed">
              LéoParts permet à chaque Congolais de financer les barrages, routes et fermes qui construisent la nation — et de percevoir des rendements.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={() => navigate("/auth?mode=register")} data-testid="hero-cta-invest"
                className="rounded-full bg-congo-gold text-slate-900 hover:bg-congo-golddark h-12 text-base font-semibold">
                Commencer à investir <ArrowRight className="h-5 w-5 ml-1" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/projets")} data-testid="hero-cta-projects"
                className="rounded-full h-12 text-base bg-white/5 text-white border-white/30 hover:bg-white/15">
                Découvrir les projets
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-2 text-blue-100 text-sm">
              <Landmark className="h-4 w-4 text-congo-gold" /> Fonds sécurisés par <span className="font-semibold text-white">CADECO</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: TrendingUp, v: "6 – 10%", l: "Rendement annuel" },
            { icon: Users, v: "50$", l: "Ticket minimum" },
            { icon: ShieldCheck, v: "100%", l: "Fonds sous séquestre" },
            { icon: Smartphone, v: "Mobile Money", l: "M-Pesa · Orange · Airtel" },
          ].map((s) => (
            <div key={s.l} className="text-center md:text-left">
              <s.icon className="h-6 w-6 text-congo-blue mx-auto md:mx-0" />
              <p className="font-head text-2xl font-bold text-slate-900 mt-2">{s.v}</p>
              <p className="text-sm text-slate-500">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-congo-blue text-center">Comment ça marche</p>
        <h2 className="font-head text-2xl sm:text-3xl font-bold text-slate-900 text-center mt-2">Investir en 4 étapes simples</h2>
        <div className="grid md:grid-cols-4 gap-6 mt-10">
          {[
            { n: "1", t: "Inscrivez-vous", d: "Créez votre compte et vérifiez votre identité (KYC) en quelques minutes." },
            { n: "2", t: "Choisissez un projet", d: "Parcourez les projets validés par CADECO : énergie, agriculture, infrastructure." },
            { n: "3", t: "Investissez dès 50$", d: "Payez par Mobile Money, carte, virement ou en agence CADECO." },
            { n: "4", t: "Suivez vos gains", d: "Recevez capital et intérêts, suivez l'impact réel de votre investissement." },
          ].map((s) => (
            <div key={s.n} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="h-10 w-10 rounded-full bg-congo-bluelight text-congo-blue font-head font-bold flex items-center justify-center">{s.n}</div>
              <h3 className="font-head font-semibold text-slate-900 mt-4">{s.t}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured projects */}
      <section className="bg-slate-50 py-16 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-congo-blue">Opportunités</p>
              <h2 className="font-head text-2xl sm:text-3xl font-bold text-slate-900 mt-2">Projets à financer</h2>
            </div>
            <Button variant="ghost" onClick={() => navigate("/projets")} className="rounded-full text-congo-blue" data-testid="see-all-projects">
              Tout voir <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {projects.map((p) => <ProjectCard key={p.id} p={p} />)}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="font-head text-2xl sm:text-3xl font-bold text-slate-900">Un partenariat de confiance</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Vos fonds sont conservés sous séquestre par la CADECO jusqu'à ce que le seuil de financement du projet soit atteint. En cas d'échec, vous êtes intégralement remboursé.
          </p>
          <ul className="mt-6 space-y-3">
            {["Fonds sécurisés par la banque CADECO", "Contrats simplifiés et signature électronique", "Traçabilité conforme à la BCC (anti-blanchiment)", "Rendements versés automatiquement aux échéances"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700"><CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />{t}</li>
            ))}
          </ul>
        </div>
        <div className="bg-congo-blue rounded-2xl p-8 text-white">
          <Star className="h-8 w-8 text-congo-gold fill-congo-gold" />
          <p className="font-head text-xl font-semibold mt-4 leading-snug">« Chaque part investie construit une école, un pont, une centrale. »</p>
          <p className="text-blue-200 text-sm mt-4">Rejoignez des milliers de Congolais qui bâtissent l'avenir du pays.</p>
          <Button onClick={() => navigate("/auth?mode=register")} className="mt-6 rounded-full bg-congo-gold text-slate-900 hover:bg-congo-golddark" data-testid="trust-cta">
            Ouvrir un compte
          </Button>
        </div>
      </section>

      <footer className="bg-congo-blue text-blue-100 py-10">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between gap-4">
          <div className="flex items-center">
            <img src="/leoparts-full.png" alt="LéoParts Capital Markets" className="h-20 w-auto object-contain" data-testid="footer-full-logo" />
          </div>
          <p className="text-sm">© 2026 LéoParts · CADECO × Studeur · République Démocratique du Congo</p>
          <div className="flex items-center gap-4">
            <a href="/faq" data-testid="footer-faq-link" className="text-sm text-slate-300 hover:text-congo-gold transition-colors">FAQ</a>
            <a href="/conditions" data-testid="footer-cgu-link" className="text-sm text-slate-300 hover:text-congo-gold transition-colors">Conditions Générales</a>
            <a href="/confidentialite" data-testid="footer-privacy-link" className="text-sm text-slate-300 hover:text-congo-gold transition-colors">Confidentialité</a>
          </div>
        </div>
      </footer>
    </Layout>
  );
}
